import React, { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { toast } from "sonner";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  getAllFilieres,
  createFiliere,
  updateFiliere,
  deleteFiliere,
} from "../../api/filieres";

import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

import withPermission from "../../core/common/autorisations/withPermission";

interface Props {
  show: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

// bouton protégée pour créer/modifier une fillière
const ProtectedBtnAddEditFiliere = withPermission(
  (props: any) => (
    <Button
      variant={props.editingId ? "primary" : "dark"}
      type="submit"
      size="sm"
      className="px-4 rounded-2"
      disabled={props.isSubmitting}
    >
      {props.isSubmitting ? (
        <span className="spinner-border spinner-border-sm me-1"></span>
      ) : props.editingId ? (
        "Modifier"
      ) : (
        "Ajouter"
      )}
    </Button>
  ),
  [
    buildPermission(Resource.FILIERE, Action.CREATE),
    buildPermission(Resource.FILIERE, Action.UPDATE),
  ],
  "masquer",
);

// bouton protégée pour modifier une fillière
const ProtectedBtnEditFiliere = withPermission(
  (props: any) => (
    <button
      className="btn btn-icon btn-sm btn-soft-primary rounded-circle"
      onClick={() => props.handleEdit(props.filiere)}
      title="Modifier"
    >
      <i className="ti ti-edit"></i>
    </button>
  ),
  [buildPermission(Resource.FILIERE, Action.UPDATE)],
  "masquer",
);

// bouton protégée pour supprimer une fillière
const ProtectedBtnDeleteFiliere = withPermission(
  (props: any) => (
    <button
      className="btn btn-icon btn-sm btn-soft-danger rounded-circle"
      onClick={() => props.handleDelete(props.filiere)}
      title="Supprimer"
    >
      <i className="ti ti-trash"></i>
    </button>
  ),
  [buildPermission(Resource.FILIERE, Action.DELETE)],
  "masquer",
);

const FiliereManagementModal: React.FC<Props> = ({
  show,
  onClose,
  onSaved,
}) => {
  const [filieres, setFilieres] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [filiereLabel, setFiliereLabel] = useState("");

  const MySwal = withReactContent(Swal);

  const fetchFilieres = async () => {
    try {
      setLoading(true);
      const response = await getAllFilieres();
      setFilieres(response.data || []);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des filières");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchFilieres();
    }
  }, [show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!filiereLabel.trim()) {
      toast.error("Le libellé est requis");
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingId) {
        await updateFiliere(editingId, { label: filiereLabel });
        toast.success("Filière modifiée avec succès");
      } else {
        await createFiliere({ label: filiereLabel });
        toast.success("Filière créée avec succès");
      }
      setFiliereLabel("");
      setEditingId(null);
      fetchFilieres();
      onSaved?.();
    } catch (error: any) {
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (filiere: any) => {
    setEditingId(filiere.id);
    setFiliereLabel(filiere.label);
  };

  const handleDelete = (filiere: any) => {
    MySwal.fire({
      title: "Supprimer la filière ?",
      text: `Êtes-vous sûr de vouloir supprimer la filière "${filiere.label}" ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#dc3545",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          await deleteFiliere(filiere.id);
          toast.success("Filière supprimée avec succès");
          fetchFilieres();
          onSaved?.();
        } catch (error: any) {
          toast.error("Erreur lors de la suppression");
        }
      }
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setFiliereLabel("");
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="fw-bold">Gestion des Filières</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white p-4">
        {/* Formulaire d'ajout/modification */}
        <div
          className="p-3 rounded-3 mb-4 border-2"
          style={{ backgroundColor: "#f3f3f3" }}
        >
          <h6 className="fw-bold mb-3">
            {editingId ? "Modifier la filière" : "Ajouter une nouvelle filière"}
          </h6>
          <Form onSubmit={handleSubmit} className="d-flex gap-2">
            <div className="flex-grow-1">
              <Form.Control
                type="text"
                placeholder="Libellé de la filière (ex: Informatique)"
                value={filiereLabel}
                onChange={(e) => setFiliereLabel(e.target.value)}
                className="form-control-sm border-2"
              />
            </div>
            <ProtectedBtnAddEditFiliere
              editingId={editingId}
              isSubmitting={isSubmitting}
            />
            {editingId && (
              <Button
                variant="outline-danger"
                size="sm"
                className="px-3 rounded-2"
                onClick={cancelEdit}
              >
                Annuler
              </Button>
            )}
          </Form>
        </div>

        {/* Liste des filières */}
        <div className="border rounded-3 overflow-hidden">
          <div
            className="p-3 border-bottom d-flex justify-content-between align-items-center"
            style={{ backgroundColor: "#f3f3f3" }}
          >
            <h6 className="mb-0 fw-bold">Liste des Filières</h6>
            <span className="badge bg-primary rounded-pill">
              {filieres.length} au total
            </span>
          </div>
          <div
            className="filiere-list-scroll"
            style={{ maxHeight: "400px", overflowY: "auto" }}
          >
            {loading ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border text-primary"
                  role="status"
                ></div>
              </div>
            ) : filieres.length > 0 ? (
              filieres.map((filiere, idx) => (
                <div
                  key={filiere.id}
                  className={`p-3 d-flex align-items-center justify-content-between ${
                    idx !== filieres.length - 1 ? "border-bottom" : ""
                  } hover-bg-light transition-all`}
                >
                  <div className="d-flex align-items-center">
                    <div className="avatar avatar-sm bg-info-transparent text-info rounded-circle me-3 d-flex align-items-center justify-content-center">
                      <i className="ti ti-books fs-16"></i>
                    </div>
                    <span className="fw-semibold text-dark">
                      {filiere.label}
                    </span>
                  </div>
                  <div className="d-flex gap-2">
                    <ProtectedBtnEditFiliere
                      filiere={filiere}
                      handleEdit={handleEdit}
                    />
                    <ProtectedBtnDeleteFiliere
                      filiere={filiere}
                      handleDelete={handleDelete}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-info-circle fs-32 mb-2 d-block"></i>
                Aucune filière trouvée.
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top">
        <Button variant="light" className="px-4 rounded-2" onClick={onClose}>
          Fermer
        </Button>
      </Modal.Footer>

      <style>{`
        .bg-info-transparent { background: rgba(13, 202, 240, 0.1); }
        .btn-soft-primary { background: rgba(13, 110, 253, 0.1); color: #0d6efd; border: none; }
        .btn-soft-danger { background: rgba(220, 53, 69, 0.1); color: #dc3545; border: none; }
        .btn-soft-primary:hover { background: #0d6efd; color: white; }
        .btn-soft-danger:hover { background: #dc3545; color: white; }
        .hover-bg-light:hover { background-color: #f8f9fa; }
        .transition-all { transition: all 0.2s ease; }
        .border-dashed { border-style: dashed !important; }
      `}</style>
    </Modal>
  );
};
export default withPermission(
  FiliereManagementModal,
  [
    ...Object.values(Action).map((action: any) =>
      buildPermission(Resource.FILIERE, action),
    ),
  ],
  "masquer",
);
